import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Divider,
  Avatar,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  ArrowBack as ArrowBackIcon,
  Store as StoreIcon,
  AdminPanelSettings as AdminIcon
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import userManagementService from '../../services/userManagementService';
import { useStore } from '../../context/StoreContext';

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { t } = useTranslation();
  const { isSuperAdmin } = useStore();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchUser();
  }, [id]);

  const fetchUser = async () => {
    try {
      setLoading(true);
      const data = await userManagementService.getUserById(id);
      setUser(data);
    } catch (error) {
      toast.error(t('common.loadError'));
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <Box sx={{ p: 3 }}>{t('common.loading')}</Box>;
  }

  if (!user) {
    return <Box sx={{ p: 3 }}>{t('common.notFound')}</Box>;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/users')}
          >
            {t('common.back')}
          </Button>
          <Typography variant="h4">{t('users.viewUser')}</Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/users/edit/${id}`)}
        >
          {t('users.editUser')}
        </Button>
      </Box>

      {user.globalRole === 'SUPER_ADMIN' && (
        <Alert severity="info" icon={<AdminIcon />} sx={{ mb: 3 }}>
          {t('users_ext.superAdminNotice')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('common.basicInfo')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <Avatar
                  sx={{ width: 80, height: 80, mr: 2, bgcolor: 'primary.main', fontSize: '2rem' }}
                >
                  {user.name?.charAt(0).toUpperCase()}
                </Avatar>
                <Box>
                  <Typography variant="h6">{user.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>

              <Grid container spacing={2}>
                {user.globalRole === 'STORE_USER' && (
                  <Grid item xs={12}>
                    <Box sx={{
                      p: 2,
                      bgcolor: 'info.lighter',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: 'info.main'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <StoreIcon color="info" />
                        <Typography variant="subtitle2" fontWeight={600}>
                          {t('users.assignedStore')}
                        </Typography>
                      </Box>
                      <Typography variant="h6" color="info.dark">
                        {user.storeName || t('users_ext.notAssigned')}
                      </Typography>
                      {user.storeCode && (
                        <Typography variant="caption" color="text.secondary">
                          {t('users_ext.storeCodeLabel', { code: user.storeCode })}
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                )}

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('users.globalRole')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={user.globalRole}
                      color={user.globalRole === 'SUPER_ADMIN' ? 'error' : 'primary'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.status')}
                  </Typography>
                  <Typography variant="body1">
                    <Chip
                      label={user.isActive ? t('common.active') : t('common.inactive')}
                      color={user.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {t('users_ext.assignedRole')}
                  </Typography>
                  <Typography variant="body1">
                    {user.roleDisplayName ? (
                      <Chip label={user.roleDisplayName} color="secondary" size="small" />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        {t('users_ext.noRoleAssigned')}
                      </Typography>
                    )}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.createdAt')}
                  </Typography>
                  <Typography variant="body2">
                    {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                </Grid>

                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    {t('common.lastLogin')}
                  </Typography>
                  <Typography variant="body2">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleDateString() : t('common.never')}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Permissions */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('users_ext.permissions')}
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('users_ext.rolePermissions', { count: user.rolePermissions?.length || 0 })}
              </Typography>
              {user.rolePermissions && user.rolePermissions.length > 0 ? (
                <Box sx={{ mb: 2, maxHeight: 200, overflow: 'auto' }}>
                  {user.rolePermissions.map((permission) => (
                    <Chip
                      key={permission.id}
                      label={permission.displayName}
                      size="small"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {t('users_ext.noRolePermissions')}
                </Typography>
              )}

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('users_ext.directPermissions', { count: user.directPermissions?.length || 0 })}
              </Typography>
              {user.directPermissions && user.directPermissions.length > 0 ? (
                <Box sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {user.directPermissions.map((permission) => (
                    <Chip
                      key={permission.id}
                      label={permission.displayName}
                      size="small"
                      color="warning"
                      sx={{ m: 0.5 }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  {t('users_ext.noDirectPermissions')}
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        {isSuperAdmin && user.globalRole === 'STORE_USER' && user.storeId && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('users_ext.storeInfo')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('common.id')}
                    </Typography>
                    <Typography variant="body1">{user.storeId}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('stores.storeName')}
                    </Typography>
                    <Typography variant="body1">{user.storeName}</Typography>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Typography variant="caption" color="text.secondary">
                      {t('stores.storeCode')}
                    </Typography>
                    <Typography variant="body1">{user.storeCode}</Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="outlined"
                      startIcon={<StoreIcon />}
                      onClick={() => navigate(`/stores/view/${user.storeId}`)}
                    >
                      {t('users_ext.viewStoreDetails')}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default UserDetails;
